/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from "react";
import { AxiosError } from "axios";
import axiosPrivate from "./axios";
import { useRefreshToken } from "./useRefrehToken";
import { useAuthStore } from "@/redux/hooks";

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

export function useAxiosPrivate() {
  const auth = useAuthStore();
  const refresh = useRefreshToken();

  useEffect(() => {
    const requestInterceptor = axiosPrivate.interceptors.request.use(
      (config) => {
        const token = auth.accessToken || localStorage.getItem("accessToken");
        if (token && !config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error?.config as any;
        const status = error?.response?.status;

        // Don't retry if already retried or not an auth error
        if (!originalRequest || originalRequest._retry || (status !== 401 && status !== 403)) {
          return Promise.reject(error);
        }

        // Don't retry refresh request itself
        if (originalRequest.url === "/twitter/refresh") {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(axiosPrivate(originalRequest));
              },
              reject: (err) => reject(err),
            });
          });
        }

        isRefreshing = true;

        try {
          const newAccessToken = await refresh();

          processQueue(null, newAccessToken ?? null);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axiosPrivate(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestInterceptor);
      axiosPrivate.interceptors.response.eject(responseInterceptor);
    };
  }, [auth.accessToken, refresh]);

  return axiosPrivate;
}
